import React from 'react';
import { AlertTriangleIcon, GiftIcon } from 'lucide-react';
import { Section } from './Shared';
import { DnaHelix, Mandala, TreeOfLife } from './BioDecor';
import { bonuses, tomes } from '../data/kitContents';
const accentStyles: Record<'blue' | 'green' | 'gold', {
  badge: string;
  title: string;
  check: string;
}> = {
  blue: {
    badge: 'bg-blue-900 text-amber-200',
    title: 'text-blue-900',
    check: 'text-blue-700'
  },
  green: {
    badge: 'bg-emerald-800 text-amber-200',
    title: 'text-emerald-800',
    check: 'text-emerald-700'
  },
  gold: {
    badge: 'bg-amber-700 text-amber-50',
    title: 'text-amber-700',
    check: 'text-amber-600'
  }
};
export function PackContents() {
  return <Section id="contenido" className="relative" data-id="element-231" data-mp="922dz0xz1k6b">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden" data-id="element-232" data-mp="90r9t00m6aeo">
        <TreeOfLife className="absolute right-[3%] top-8 h-40 w-24 text-emerald-50" data-id="element-233" data-mp="wqvd169w4855" />
        <DnaHelix className="absolute left-[2%] top-1/2 h-48 w-20 text-blue-50" data-id="element-234" data-mp="11lhpzdvme8rc" />
      </div>

      <div className="mb-4 text-center" data-id="element-235" data-mp="3p7o131aobm5w">
        <p className="mb-2 text-sm font-bold uppercase tracking-[.18em] text-amber-700" data-id="element-236" data-mp="3k8thvy8snvl9">✨ A complete practitioner toolkit</p>
        <h2 className="text-3xl font-bold text-blue-900 md:text-5xl" data-id="element-237" data-mp="2sv25wjfas14">
          3 Core Volumes + 5 Strategic Bonuses
        </h2>
        <div className="mt-4 flex flex-wrap justify-center gap-3 text-sm font-semibold text-slate-700 md:text-base" data-id="element-238" data-mp="3u566ihphfj95">
          <span className="rounded-full border border-slate-200 bg-white px-4 py-1.5" data-id="element-239" data-mp="bub55j7wrrvb">📚 3 Volumes + 5 Bonuses</span>
          <span className="rounded-full border border-amber-300 bg-white px-4 py-1.5" data-id="element-240" data-mp="lqe3edzn36cz">📖 245+ pages of practical resources</span>
        </div>
      </div>

      <p className="mx-auto mb-10 mt-6 max-w-2xl text-center text-sm leading-relaxed text-slate-700 md:text-base" data-id="element-241" data-mp="gscm9ylrxh45">
        Whether you are beginning in biodecoding or already working with clients and want to do so with more
        structure, confidence, and professional clarity, the{' '}
        <strong className="text-emerald-700" data-id="element-242" data-mp="388o2jr3awahc">Biodecoding Practitioner Kit™ was created for you</strong>.
      </p>

      <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-3" data-id="element-243" data-mp="3c8yk810ggpc1">
        {tomes.map(tome => {
        const accent = accentStyles[tome.accent];
        return <article key={tome.label} className="flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-sm md:p-7" data-id="element-244" data-mp="3m74kyfaizpfb">
              <div className="mb-4 flex gap-4" data-id="element-245" data-mp="3r5panks5iqpi">
                <img src={tome.cover} alt={tome.coverAlt} loading="lazy" draggable={false} className="h-[150px] w-[112px] shrink-0 rounded-lg object-cover object-top shadow-md ring-1 ring-slate-200" data-id="element-246" data-mp="3v7127vul5m0c" />
                <div className="min-w-0" data-id="element-247" data-mp="8shnfy1p84tc">
                  <span className={`inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-1.5 text-[11px] font-bold tracking-[.18em] ${accent.badge}`} data-id="element-248" data-mp="8rga1mx7n68g">
                    <span aria-hidden="true" data-id="element-249" data-mp="ipcpgqx6kncd">{tome.emoji}</span>
                    {tome.label}
                  </span>
                  <h3 className={`mt-3 text-lg font-bold leading-tight md:text-xl ${accent.title}`} data-id="element-250" data-mp="238yc0i87lj4n">
                    {tome.title}
                  </h3>
                  <p className="mt-1.5 text-xs italic leading-snug text-slate-500" data-id="element-251" data-mp="1t9qtzhqj50b7">{tome.subtitle}</p>
                </div>
              </div>
              <div className="mb-4 h-px bg-amber-500/50" aria-hidden="true" data-id="element-252" data-mp="1ocgxxj8mxqee" />
              <ul className="space-y-2.5 text-sm leading-snug text-slate-900" data-id="element-253" data-mp="2lyxwssx0oc29">
                {tome.features.map(feature => <li key={feature} className="flex gap-2.5" data-id="element-254" data-mp="2h0wb0087hyn3">
                    <span className={`font-bold ${accent.check}`} data-id="element-255" data-mp="2c8ld12x10mx6">✓</span>
                    <span data-id="element-256" data-mp="2c6s5llel9yo4">{feature}</span>
                  </li>)}
              </ul>
              {tome.note && <p className="mt-auto pt-4" data-id="element-257" data-mp="277p7ym0xuuq6">
                  <span className="block rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium leading-relaxed text-slate-900" data-id="element-258" data-mp="34pfiwencg9en">
                    {tome.note}
                  </span>
                </p>}
              {tome.warning && <p className="mt-3 flex gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-[12px] leading-relaxed text-slate-700" data-id="element-259" data-mp="2kf4lvejabvl8">
                  <AlertTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" data-id="element-260" data-mp="2airs6gnqphqh" />
                  <span data-id="element-261" data-mp="2fh30uo2atm74">{tome.warning}</span>
                </p>}
            </article>;
      })}
      </div>

      <div className="mb-6 mt-14 text-center" data-id="element-262" data-mp="336dvrowywmdc">
        <h3 className="text-xl font-bold text-blue-900 md:text-2xl" data-id="element-263" data-mp="337fhcnbm0r9b">
          🎁 5 STRATEGIC BONUSES — <span className="text-amber-700" data-id="element-264" data-mp="385h3jh4c16p0">ALWAYS INCLUDED</span>
        </h3>
      </div>

      <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-265" data-mp="2tgckjxppqyfu">
        {bonuses.map(bonus => <article key={bonus.number} className="flex gap-4 rounded-2xl border border-blue-100 bg-blue-50 p-4" data-id="element-266" data-mp="2yemek4zpbszv">
            <img src={bonus.cover} alt={bonus.coverAlt} loading="lazy" draggable={false} className="h-[124px] w-[93px] shrink-0 rounded-lg object-cover object-top shadow-md ring-1 ring-slate-200" data-id="element-267" data-mp="3m4grsnnxnjkn" />
            <div className="flex min-w-0 flex-col" data-id="element-268" data-mp="1ffok5pxi55cn">
              <div className="mb-2 flex flex-wrap items-center gap-2" data-id="element-269" data-mp="1aiy0ksmhnrmh">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-900 px-2.5 py-1 text-[10px] font-bold tracking-wide text-amber-200" data-id="element-270" data-mp="1p7cf40t9p4iv">
                  <GiftIcon className="h-3 w-3" data-id="element-271" data-mp="1k8rei0a3sitw" />
                  BONUS {bonus.number}
                </span>
                <span className="text-[11px] font-semibold text-amber-700" data-id="element-272" data-mp="wix3lo9ch4ca">{bonus.pages}</span>
              </div>
              <p className="text-[15px] font-bold leading-tight text-blue-900" data-id="element-273" data-mp="rlolfno750b1">{bonus.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700" data-id="element-274" data-mp="15h8rhiitto11">{bonus.description}</p>
              {bonus.features.length > 0 && <ul className="mt-3 space-y-1.5 text-[13px] leading-snug text-slate-900" data-id="element-275" data-mp="2gy8h26dshord">
                  {bonus.features.map(feature => <li key={feature} className="flex gap-2" data-id="element-276" data-mp="2cxoiqzhhcf3n">
                      <span className="font-bold text-blue-700" data-id="element-277" data-mp="2cz9id3inio8d">✓</span>
                      <span data-id="element-278" data-mp="19lr9irjkosko">{feature}</span>
                    </li>)}
                </ul>}
            </div>
          </article>)}
      </div>

      <div className="relative mt-12 text-center" data-id="element-279" data-mp="1jk708teotztn">
        <Mandala aria-hidden="true" className="pointer-events-none absolute -top-6 left-1/2 h-10 w-10 -translate-x-1/2 text-amber-400/40" data-id="element-280" data-mp="1jkouxllpc5zg" />
        <p className="inline-block rounded-full border border-amber-300 bg-white px-5 py-3 text-sm font-medium text-slate-900" data-id="element-281" data-mp="qvhxyr1wbogb">
          Everything included with your{' '}
          <span className="font-bold text-emerald-700" data-id="element-282" data-mp="vuuih55aojfq">Biodecoding Practitioner Kit™</span>
        </p>
      </div>
    </Section>;
}